/**
 * GameTimers - manages game timer execution
 */

import { Timer } from '../models/Timer';
import { Finder } from '../utils/Finder';
import { Globals } from '../core/Globals';
import { gameController } from '../core/GameController';

declare let TheGame: any;

// Forward declarations
declare namespace GameActions {
    function processActionAsync(action: any, immediate: boolean, obj?: any): Promise<void>;
}
declare namespace GameUI {
    function refreshPanelItems(): void;
    function displayLiveTimers(): void;
}
declare namespace Logger {
    function logExecutingTimer(timer: Timer): void;
}

// Store timeout handles and start times for live timers
interface LiveTimerHandle {
    timeoutId: number;
    startTime: number;
    scheduledTime: number;
}

const liveTimerHandles = new Map<string, LiveTimerHandle>();
let countdownIntervalId: number | null = null;

export const GameTimers = {
    activeLiveTimers: function (): Timer[] {
        // Do not use this function to execute timers
        if (!TheGame) {
            return [];
        }
        return TheGame.Timers.filter((timer: Timer) => {
            return timer.Active && timer.LiveTimer;
        });
    },

   runSingleTimerAsync: async function (timer: Timer, checkActive: boolean): Promise<void> {
        await this.runTimerAsync(timer, checkActive);
    },

    runSingleTimer: function (timer: Timer, checkActive: boolean): void {
        this.runTimer(timer, checkActive, () => {
            if ((timer as any)._wasReset) {
                GameTimers.runSingleTimer(timer, checkActive);
            }
        });
    },

    runTimerEventsAsync: async function (): Promise<void> {
        if (Globals.bRunningTimers || !TheGame) {
            return;
        }

        Globals.bRunningTimers = true;
        for (const timer of TheGame.Timers) {
            if (timer != null && timer.Active && !timer.LiveTimer) {
                Logger.logExecutingTimer(timer);
                await GameTimers.runSingleTimerAsync(timer, true);
            }
        }
        Globals.bRunningTimers = false;
        GameUI.refreshPanelItems();
        Globals.bTimersExecutedThisTurn = false;
    },

    runTimerEvents: function (): void {
        void this.runTimerEventsAsync();
    },

    waitForQueueEmptyAndResetTimerFlag: function (): void {
        Globals.bTimersExecutedThisTurn = false;
    },

    runTimer: function (timer: Timer, checkActive: boolean, callback?: () => void): void {
        if (!callback) {
            callback = function () { };
        }

        (timer as any)._wasReset = false;
        if (checkActive) {
            if (!timer.Active) {
                return;
            }

            timer.TurnNumber++;
            if (timer.TurnNumber > timer.Length && timer.TType == "TT_LENGTH") {
                if (!timer.Restart)
                    timer.Active = false;
                timer.TurnNumber = 0;
                return;
            }
        }

        let tempact = Finder.action(timer.Actions, "<<On Each Turn>>");
        if (tempact != null) {
            if (timer.LiveTimer) {
                Globals.runningLiveTimerCommands = true;
                (window as any).GameActions?.processActionAsync?.(tempact, true).then(() => {
                    Globals.runningLiveTimerCommands = false;
                });
                return;
            } else {
                (window as any).GameActions?.processActionAsync?.(tempact, false).then(() => {
                    afterTimerAction();
                });
                return;
            }
        }

        (window as any).UpdateStatusBars?.();
        if (tempact == null) afterTimerAction();

        function afterTimerAction() {
            if ((timer as any)._wasReset) {
                return callback!();
            }
            if (!timer.Active) {
                return;
            }

            let tempact = Finder.action(timer.Actions, "<<On Turn " + timer.TurnNumber.toString() + ">>");
            if (tempact != null) {
                (window as any).GameActions?.processActionAsync?.(tempact, false).then(() => {
                    afterTimerAction2();
                });
                return;
            }
            (window as any).UpdateStatusBars?.();

            if (tempact == null) afterTimerAction2();

            function afterTimerAction2() {
                if ((timer as any)._wasReset) {
                    return callback!();
                }
                if (!timer.Active) {
                    return;
                }

                let tempact = null;
                if (timer.TurnNumber == timer.Length && timer.TType == "TT_LENGTH") {
                    tempact = Finder.action(timer.Actions, "<<On Last Turn>>");
                    const finalize = () => {
                        if ((timer as any)._wasReset) {
                            // Timer action reset us; leave state alone.
                        } else if (timer.Restart) {
                            timer.TurnNumber = 0;
                        } else {
                            timer.Active = false;
                            timer.TurnNumber = 0;
                            if (timer.LiveTimer) {
                                GameTimers.cancelLiveTimer(timer.Name);
                            }
                        }
                    };
                    if (tempact != null) {
                        (window as any).GameActions?.processActionAsync?.(tempact, false).then(() => {
                            finalize();
                            callback!();
                        });
                        return;
                    }
                    finalize();
                }
                (window as any).UpdateStatusBars?.();

                if (tempact == null) callback?.();
            }
        }
    },

    runTimerAsync: async function (timer: Timer, checkActive: boolean): Promise<void> {
        if (checkActive) {
            if (!timer.Active) {
                return;
            }

            timer.TurnNumber++;
            if (timer.TurnNumber > timer.Length && timer.TType == "TT_LENGTH") {
                if (!timer.Restart)
                    timer.Active = false;
                timer.TurnNumber = 0;
                return;
            }
        }

        let tempact = Finder.action(timer.Actions, "<<On Each Turn>>");
        if (tempact != null) {
            await (window as any).GameActions?.processActionAsync?.(tempact, true);
        }

        (window as any).UpdateStatusBars?.();

        if ((timer as any)._wasReset) {
            return;
        }

        if (!timer.Active) {
            return;
        }

        tempact = Finder.action(timer.Actions, "<<On Turn " + timer.TurnNumber.toString() + ">>");
        if (tempact != null) {
            await (window as any).GameActions?.processActionAsync?.(tempact, false);
        }
        (window as any).UpdateStatusBars?.();

        if ((timer as any)._wasReset) {
            return;
        }

        if (!timer.Active) {
            return;
        }

        if (timer.TurnNumber == timer.Length && timer.TType == "TT_LENGTH") {
            tempact = Finder.action(timer.Actions, "<<On Last Turn>>");
            if (tempact != null) {
                await (window as any).GameActions?.processActionAsync?.(tempact, false);
            }
            // Deactivate (or wrap) the timer immediately so a save taken
            // between now and the next tick won't refire it on load.
            if ((timer as any)._wasReset) {
                // The timer action explicitly reset us; leave state alone.
            } else if (timer.Restart) {
                timer.TurnNumber = 0;
            } else {
                timer.Active = false;
                timer.TurnNumber = 0;
                if (timer.LiveTimer) {
                    this.cancelLiveTimer(timer.Name);
                }
            }
        }
    },

    tickLiveTimers: function (skipRefresh?: boolean): void {
        // Legacy function - kept for API compatibility but no longer used
        // Live timers now use setTimeout per timer
    },

    scheduleLiveTimers: function (oneSecond: number): void {
        // Legacy function - kept for API compatibility
        // Live timers are now scheduled individually when they become active
        // This function is called during initialization but does nothing
        // Individual timers are scheduled via scheduleLiveTimer
    },

    scheduleLiveTimer: function (timer: Timer): void {
        if (!timer.Active || !timer.LiveTimer) {
            return;
        }

        // Cancel existing timeout if any
        this.cancelLiveTimer(timer.Name);

        // Calculate remaining time
        const now = Date.now();
        const elapsed = timer.curtickcount || 0;
        const remaining = Math.max(0, (timer.TimerSeconds * 1000) - elapsed);
        
        if (remaining <= 0) {
            // Timer should fire immediately
            timer.curtickcount = 0;
            GameTimers.runTimer(timer, true);
            return;
        }

        // Store start time and scheduled time
        const scheduledTime = now + remaining;
        const handle: LiveTimerHandle = {
            timeoutId: window.setTimeout(() => {
                timer.curtickcount = 0;
                liveTimerHandles.delete(timer.Name);
                // If the game is currently awaiting input, queue this live timer
                if (gameController.gameAwaitingInput) {
                    gameController.queueLiveTimer(timer);
                } else {
                    GameTimers.runTimer(timer, true);
                }
                // If timer restarts, reschedule it
                if (timer.Active && timer.LiveTimer) {
                    GameTimers.scheduleLiveTimer(timer);
                }
                GameUI.refreshPanelItems();
            }, remaining),
            startTime: now,
            scheduledTime: scheduledTime
        };

        liveTimerHandles.set(timer.Name, handle);
    },

    cancelLiveTimer: function (timerName: string): void {
        const handle = liveTimerHandles.get(timerName);
        if (handle) {
            clearTimeout(handle.timeoutId);
            liveTimerHandles.delete(timerName);
        }
    },

    skipLiveTimer: function (timerName: string): void {
        const timer = Finder.timer(timerName);
        if (!timer || !timer.Active || !timer.LiveTimer) {
            return;
        }

        // Cancel the scheduled timeout
        this.cancelLiveTimer(timerName);

        // Execute immediately
        timer.curtickcount = 0;
        GameTimers.runTimer(timer, true);
        
        // If timer restarts, reschedule it
        if (timer.Active && timer.LiveTimer) {
            GameTimers.scheduleLiveTimer(timer);
        }
        
        GameUI.refreshPanelItems();
    },

    getLiveTimerRemaining: function (timer: Timer): number {
        const handle = liveTimerHandles.get(timer.Name);
        if (!handle) {
            // Timer not scheduled, calculate from curtickcount
            const elapsed = timer.curtickcount || 0;
            return Math.max(0, (timer.TimerSeconds * 1000) - elapsed);
        }
        
        // Calculate remaining time from scheduled time
        const now = Date.now();
        return Math.max(0, handle.scheduledTime - now);
    },

    startCountdownDisplay: function (): void {
        // Start interval to update countdown displays
        if (countdownIntervalId !== null) {
            return; // Already running
        }

        countdownIntervalId = window.setInterval(() => {
            GameUI.displayLiveTimers();
        }, 100); // Update every 100ms for smooth countdown
    },

    stopCountdownDisplay: function (): void {
        if (countdownIntervalId !== null) {
            clearInterval(countdownIntervalId);
            countdownIntervalId = null;
        }
    },

    rescheduleAllLiveTimers: function (): void {
        // Reschedule all active live timers (useful when timers are added/modified)
        if (!TheGame) {
            return;
        }

        const activeTimers = this.activeLiveTimers();
        const scheduledNames = new Set(liveTimerHandles.keys());
        
        // Cancel timers that are no longer active
        scheduledNames.forEach((timerName) => {
            const timer = Finder.timer(timerName);
            if (!timer || !timer.Active || !timer.LiveTimer) {
                this.cancelLiveTimer(timerName);
            }
        });

        // Schedule timers that are active but not yet scheduled
                      activeTimers.forEach((timer: Timer) => {
                          if (!liveTimerHandles.has(timer.Name)) {
                GameTimers.scheduleLiveTimer(timer);
            }
        });
    },

    /**
     * Deactivate any timers that have exceeded their length but are still marked active.
     * This handles the case where a save was created with a timer at TurnNumber >= Length.
     * Called after loading a save to ensure timer state is consistent.
     */
    deactivateExpiredTimers: function (): void {
        if (!TheGame) {
            return;
        }
        for (const timer of TheGame.Timers) {
            if (!timer || !timer.Active) {
                continue;
            }
            // Only apply to length-based timers (TT_LENGTH)
            if (timer.TType === "TT_LENGTH" && timer.TurnNumber >= timer.Length) {
                if (!timer.Restart) {
                    timer.Active = false;
                    timer.TurnNumber = 0;
                    if (timer.LiveTimer) {
                        this.cancelLiveTimer(timer.Name);
                    }
                }
            }
        }
    },

    /**
     * Heal "stuck-on" fire-once timers in old saves.
     *
     * Older versions of regalia (<= 2.3.1) had bugs that left non-restart
     * TT_LENGTH timers in Active=true state after they had already fired,
     * because the engine never wrote Active=false back. A save taken in that
     * state then resurrects the timer on load and it fires again.
     *
     * We can't recover the original execution log from a diff, but we can
     * statically detect timers that are *designed* to fire at most once and
     * heal them. A timer is considered fire-once and safely healable if ALL
     * of the following hold for its pristine design-time definition:
     *
     *   - !Restart (does not automatically restart)
     *   - !Active (not initially active)
     *   - No action that references the timer uses an advanced (custom JS)
     *     script, and no owner uses ActionsUseAdvanced. Advanced scripts are
     *     opaque to static analysis, so we conservatively bail out.
     *
     * We deliberately do NOT require the absence of a deactivation path:
     * many fire-once timers are turned off only on a specific ending or
     * branch that the player may never reach. We also do not require an
     * explicit activation path — any inactive, non-restart timer that ends
     * up Active in an older save is treated as suspect.
     *
     * For each such timer that is currently Active in the loaded TheGame and
     * has not been explicitly reset (_wasReset !== true), we flip it back to
     * Active=false and reset TurnNumber/curtickcount.
     *
     * This is intentionally narrow: it never disables a timer that the game
     * design ever turns off itself, and it never disables a timer that was
     * initially active. The opposite-direction risk (a timer that should
     * still be running on load) is bounded to TT_LENGTH non-restart fire-once
     * timers, which by definition either fire on the very next tick or do
     * nothing thereafter — losing one such pending fire is far less harmful
     * than firing it repeatedly across every load.
     */
    healSuspiciousFireOnceTimers: function (): number {
        if (!TheGame || !Array.isArray(TheGame.Timers)) {
            return 0;
        }
        const OriginalGame = (window as any).OriginalGame;
        if (!OriginalGame || !Array.isArray(OriginalGame.Timers)) {
            return 0;
        }

        type Classification = {
            fireOnce: boolean;
            opaque: boolean;
            activatedSomewhere: boolean;
            deactivatedSomewhere: boolean;
            resetSomewhere: boolean;
        };

        const classifications = new Map<string, Classification>();
        for (const t of OriginalGame.Timers) {
            if (!t || typeof t.Name !== 'string') continue;
            classifications.set(t.Name, {
                fireOnce: false,
                opaque: false,
                activatedSomewhere: false,
                deactivatedSomewhere: false,
                resetSomewhere: false,
            });
        }
        if (classifications.size === 0) {
            return 0;
        }

        const markAdvanced = (timerName: string): void => {
            const c = classifications.get(timerName);
            if (c) c.opaque = true;
        };

        // Recursively walk an array of (Command | Condition) entries.
        const walkCmdList = (
            list: any[] | undefined,
            ownerAdvanced: boolean,
            actionAdvanced: boolean,
        ): void => {
            if (!Array.isArray(list)) return;
            for (const entry of list) {
                if (!entry) continue;
                if (typeof entry.cmdtype === 'string') {
                    const cmd = entry;
                    const type = cmd.cmdtype;
                    if (type === 'CT_SETTIMER' || type === 'CT_RESETTIMER') {
                        const timerName = cmd.CommandPart2;
                        if (typeof timerName === 'string' && classifications.has(timerName)) {
                            const c = classifications.get(timerName)!;
                            if (ownerAdvanced || actionAdvanced) {
                                c.opaque = true;
                            }
                            if (type === 'CT_SETTIMER') {
                                if (cmd.CommandPart3 === 'Active') {
                                    c.activatedSomewhere = true;
                                } else {
                                    c.deactivatedSomewhere = true;
                                }
                            } else {
                                c.resetSomewhere = true;
                            }
                        }
                    }
                    // CT_EXECUTETIMER does not change Active state; ignore.
                } else if (Array.isArray((entry as any).PassCommands) || Array.isArray((entry as any).FailCommands)) {
                    // Condition node
                    walkCmdList((entry as any).PassCommands, ownerAdvanced, actionAdvanced);
                    walkCmdList((entry as any).FailCommands, ownerAdvanced, actionAdvanced);
                }
            }
        };

        const walkActions = (actions: any[] | undefined, ownerAdvanced: boolean): void => {
            if (!Array.isArray(actions)) return;
            for (const action of actions) {
                if (!action) continue;
                const actionAdvanced = Boolean(action.UseAdvancedScript);
                walkCmdList(action.PassCommands, ownerAdvanced, actionAdvanced);
                walkCmdList(action.FailCommands, ownerAdvanced, actionAdvanced);
                if (Array.isArray(action.Conditions)) {
                    for (const cond of action.Conditions) {
                        if (!cond) continue;
                        walkCmdList(cond.PassCommands, ownerAdvanced, actionAdvanced);
                        walkCmdList(cond.FailCommands, ownerAdvanced, actionAdvanced);
                    }
                }
            }
        };

        const visitOwners = (owners: any[] | undefined): void => {
            if (!Array.isArray(owners)) return;
            for (const owner of owners) {
                if (!owner) continue;
                walkActions(owner.Actions, Boolean(owner.ActionsUseAdvanced));
            }
        };

        visitOwners(OriginalGame.Rooms);
        visitOwners(OriginalGame.Objects);
        visitOwners(OriginalGame.Characters);
        visitOwners(OriginalGame.Timers);
        if (OriginalGame.Player) {
            walkActions(OriginalGame.Player.Actions, Boolean(OriginalGame.Player.ActionsUseAdvanced));
        }

        // Determine fire-once classification for each timer.
        for (const t of OriginalGame.Timers) {
            if (!t || typeof t.Name !== 'string') continue;
            const c = classifications.get(t.Name);
            if (!c) continue;
            const initiallyInactive = !t.Active;
            const isLength = t.TType === 'TT_LENGTH';
            const noRestart = !t.Restart;
            // Broad rule: any timer that is designed to start inactive and
            // does not automatically restart is treated as suspicious if it
            // ends up Active in an older save. The only escape hatch is
            // opacity from advanced (custom JS) command code paths involving
            // this timer — in that case we cannot reason about whether the
            // script is supposed to leave it running, so we leave it alone.
            // We intentionally do NOT require a deactivation path to be
            // absent: many fire-once timers are deactivated only by an
            // ending/branch-specific path that the player may never trigger.
            // We also keep the TT_LENGTH gate so that live (real-time)
            // timers and other exotic types are not silently disabled.
            void isLength;
            c.fireOnce =
                initiallyInactive &&
                noRestart &&
                !c.opaque;
        }

        let healed = 0;
        for (const timer of TheGame.Timers) {
            if (!timer || typeof timer.Name !== 'string') continue;
            const c = classifications.get(timer.Name);
            if (!c || !c.fireOnce) continue;
            if (!timer.Active) continue;
            if ((timer as any)._wasReset === true) continue;
            // It is a fire-once timer and somehow ended up active in the save.
            // The design provides no way to turn it off; heal it.
            timer.Active = false;
            timer.TurnNumber = 0;
            (timer as any).curtickcount = 0;
            if (timer.LiveTimer) {
                this.cancelLiveTimer(timer.Name);
            }
            healed++;
        }

        if (healed > 0) {
            try {
                console.warn(`[regalia] Healed ${healed} suspicious fire-once timer(s) from an older save.`);
            } catch { /* ignore */ }
        }
        return healed;
    }
};

// Make available globally
(window as any).GameTimers = GameTimers;
